import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Medal, Trophy, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import type { GameCode } from '../../entities/room/types';
import {
  fetchPublicProfile,
  followUser,
  unfollowUser,
  type LeaderboardBoard,
  type LeaderboardItem,
  type LeaderboardLastBet,
  type LeaderboardPeriod,
  type LeaderboardSort,
  type LeaderboardUser,
  type PublicProfileBet,
  type PublicProfilePeriod,
  type PublicProfileResponse,
} from '../leaderboard/api';
import { useAuthStore } from '../auth/authStore';
import { GAME_LABELS, gameNameKey, pickLabel } from '../../shared/game/betOptions';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtMoney, fmtInt } from '../../shared/lib/format';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';

/**
 * 玩家公开资料 sheet。**从 `pages/tabs/LeaderboardPage.tsx` 抽出来的**，一行未改
 * 逻辑，只是换了住处并导出。
 *
 * <p>抽它的原因不是文件太大，而是 Milhar 买家名单要能点头像看人，而
 * `activeProfileUserId` 只在 `route === \'leaderboard\'` 时生效
 * （`appStore.ts:175`）。抽奖当年的解法是「跳到排行榜页开 sheet，关掉再跳回来」，
 * 那套放在选号页上会丢光 pick / 数量 / teimosinha —— 它们都是组件本地 state。
 * 见 `doc/design/MILHAR_ATMOSPHERE_SPEC.md` §3.2。
 *
 * <p>`boardPeriod` / `roomNames` / `rank` 是排行榜特有的语境，在别处可以不给。
 */

export type UiLeaderboardItem = {
  rank: number;
  ranked: boolean;
  user: {
    id: string;
    username: string;
    initial: string;
    flag: string;
    level: number;
    avatarUrl?: string;
    publicLeaderboard?: boolean;
    isAdmin?: boolean;
  };
  plays: number;
  wins: number;
  winRate: number;
  profit: number;
  roi: number;
  streak: number;
  online: boolean;
  roomCode: string;
  lastBet: LeaderboardLastBet | null;
  copyRoomCode: string;
  isMe: boolean;
  // board=wins only.
  game: string;
  tier: string | null;
  roundNumber: number | null;
  // The server's label; Milhar rows are renumbered locally from `roundStartsAt`
  // (see bigWinRoundLabel) because the round number follows the viewer's clock.
  roundLabel: string;
  roundStartsAt: number | null;
  isJackpot: boolean;
  eventId: string | null;
  payout: number;
  // Draw is a free entry: these two must allow null, never a bare number.
  stake: number | null;
  multiple: number | null;
  wonAt: number;
};

export type RoomNames = Record<string, string>;

export function PublicProfileSheet({
  user,
  // 排行榜的语境：用哪个时间窗打开、房间名怎么显示。别处（如 Milhar 买家名单）
  // 没有「板子的时间窗」这回事，给默认值即可 —— 不要为此在调用方伪造一个。
  boardPeriod = '1h',
  roomNames = {},
  // Same escape hatch as GuestAuthSheet: a caller that is itself a fixed
  // full-screen layer above the plain `sheet-backdrop` z-index (the draws
  // "Who took part" screen sits at 70) passes a modifier so the profile
  // lands on top instead of underneath it.
  backdropClassName = 'sheet-backdrop',
  onClose,
}: {
  user: UiLeaderboardItem;
  boardPeriod?: LeaderboardPeriod;
  roomNames?: RoomNames;
  backdropClassName?: string;
  onClose: () => void;
}) {
  const token = useAuthStore((state) => state.token);
  const t = useI18nStore((state) => state.t);
  // Open in the same lens the board was filtered by: ranking someone on the
  // last hour and then judging them on a 7-day total is two questions stacked
  // on one screen. 7d stays one tap away as the "is this a real player" view.
  const [period, setPeriod] = useState<PublicProfilePeriod>(profilePeriodFor(boardPeriod));
  useEffect(() => setPeriod(profilePeriodFor(boardPeriod)), [boardPeriod]);
  const profileId = profileApiUserId(user.user);
  const profileQuery = useQuery({
    queryKey: ['public-profile', profileId || user.user.id, period],
    // 20 is what the endpoint will give: it clamps anything larger. Five rows
    // was too thin a sample to read a player by — one lucky hit filled the
    // whole list — and the sheet scrolls anyway.
    queryFn: () => fetchPublicProfile({ token, userId: profileId, period, limit: 20 }),
    // A guest never loads the profile: the sheet below shows the gate instead
    // (and the endpoint answers 401 without a bearer anyway).
    enabled: !!profileId && !!token,
  });
  const mergedUser = normalizeProfileUser(user, profileQuery.data?.user);
  const stats = profileStats(profileQuery.data);
  const visible = profileStatsVisible(profileQuery.data);
  const bets = profileBets(profileQuery.data);
  const raffle = profileRaffle(profileQuery.data);
  const followersCount = profileFollowers(profileQuery.data);
  const [showAllBets, setShowAllBets] = useState(false);
  const isFollowing = profileIsFollowing(profileQuery.data);
  const isSelf = user.isMe || profileIsSelf(profileQuery.data);
  const canFollow = !!profileId && !isSelf && !!token;
  // A guest sees the same button; tapping it goes to sign-up. Hiding it made
  // the sheet look like following did not exist, and the guest is exactly the
  // person a "follow this player" moment should convert.
  const guestFollow = !!profileId && !isSelf && !token;
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const setRoute = useAppStore((state) => state.setRoute);
  // Live position beats the row snapshot: the sheet may be open for a while.
  const presence = profilePresence(profileQuery.data, user);

  const queryClient = useQueryClient();
  const followMutation = useMutation({
    mutationFn: () => (isFollowing ? unfollowUser(token, profileId) : followUser(token, profileId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['public-profile'] }),
  });

  // Product rule (PM, 2026-09-14): a guest may see a player's name and avatar
  // wherever they appear — hall feed, draw entries, Milhar buyers, ranking,
  // room table — but never the profile behind them. Every entry point ends up
  // in this sheet, so the gate lives here once instead of in six callers.
  if (!token) {
    return (
      <div className={backdropClassName} role="presentation" onMouseDown={onClose}>
        <section className="profile-sheet public-profile-sheet guest-auth-sheet-react" role="dialog" aria-modal="true" aria-label={t('guestProfileSheetTitle')} onMouseDown={(event) => event.stopPropagation()} data-user-profile-sheet-react="true" data-guest-profile-gate="true">
          <div className="sheet-grabber" />
          <header className="sheet-header">
            <div>
              <h2>{t('guestProfileSheetTitle')}</h2>
              <p>{t('guestProfileSheetBody')}</p>
            </div>
            <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
              <X size={18} aria-hidden="true" />
            </button>
          </header>
          <section className="public-profile-hero">
            <span className="leaderboard-avatar profile">
              {user.user.avatarUrl ? <img src={user.user.avatarUrl} alt="" /> : user.user.initial}
            </span>
            <strong>@{user.user.username}</strong>
          </section>
          <div className="guest-auth-actions-react">
            <button className="primary-button" type="button" onClick={() => { clearGuest(); setRoute('auth.register'); }}>{t('guestSheetSignup')}</button>
            <button className="secondary-button" type="button" onClick={() => { clearGuest(); setRoute('auth.login'); }}>{t('guestSheetLogin')}</button>
            <button className="link-button" type="button" onClick={onClose}>{t('guestSheetCancel')}</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={backdropClassName} role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet public-profile-sheet" role="dialog" aria-modal="true" aria-label={t('publicProfileTitle')} onMouseDown={(event) => event.stopPropagation()} data-user-profile-sheet-react="true">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('publicProfileTitle')}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {/* Everything below scrolls as one column. The old sheet pinned every
            block and left the bet list to fight for the remainder, which on a
            phone was zero pixels — the one thing a copier needs was unreachable. */}
        <div className="public-profile-scroll">
          <section className="public-profile-hero">
            <span className={`leaderboard-avatar profile ${presence.online ? 'is-online' : ''}`}>
              {mergedUser.avatarUrl ? <img src={mergedUser.avatarUrl} alt="" /> : mergedUser.initial}
            </span>
            <strong>@{mergedUser.username}</strong>
            <small>VIP {mergedUser.level}</small>
            <div className="public-profile-chip-row-react">
              {user.isMe ? <em>{t('chipYou')}</em> : null}
              {/* The rank came from the board's window. Once the reader switches
                  this sheet to another period, every number below changes but
                  the badge would not — "#1" beside a 7-day total is a claim
                  about a different hour. It only stands while the two match. */}
              {user.rank > 0 && period === profilePeriodFor(boardPeriod) ? <em>#{user.rank} · {periodLabel(boardPeriod, t)}</em> : null}
              {mergedUser.isAdmin ? <em>{t('publicProfileRoomAdmin')}</em> : null}
              {!profileId ? <em>{t('publicProfileUnavailable')}</em> : null}
              {profileId && !profileQuery.isLoading && !profileQuery.isError && !visible ? <em>{t('publicProfilePrivate')}</em> : null}
            </div>
            <div className="public-profile-follow-row">
              {canFollow ? (
                <button
                  type="button"
                  className={`profile-follow-btn ${isFollowing ? 'following' : ''}`}
                  onClick={() => followMutation.mutate()}
                  disabled={followMutation.isPending}
                  aria-pressed={isFollowing}
                >
                  {isFollowing ? t('profileFollowing') : t('profileFollow')}
                </button>
              ) : null}
              {guestFollow ? (
                <button type="button" className="profile-follow-btn" onClick={() => { clearGuest(); setRoute('auth.register', { replace: true }); }}>
                  {t('profileFollow')}
                </button>
              ) : null}
              {/* The count stays even at zero: how many people follow someone is
                  part of judging whether they are worth following. */}
              <span className="profile-followers-count">
                <b>{fmtInt(followersCount)}</b> {followersCount === 1 ? t('profileFollowerOne') : t('profileFollowers')}
              </span>
            </div>
          </section>

          {/* The whole point of the sheet: he is at a table right now and this
              is what he just played. Without it the profile is a tombstone. */}
          {presence.online && !isSelf ? (
            <section className={`public-profile-live ${presence.copyRoomCode ? 'is-copyable' : ''}`}>
              <span className="public-profile-live-head">
                <em className="leaderboard-live-dot" aria-hidden="true" />
                {t('leaderboardPlayingIn').replace('%room%', roomNames[presence.roomCode] || presence.roomCode)}
              </span>
              {presence.lastBet?.tab && presence.lastBet?.pick ? (
                <span className="public-profile-live-pick">
                  {t('publicProfileLastPick')}:{' '}
                  <b>{pickLabel((presence.lastBet.game || 'lastdigit') as GameCode, presence.lastBet.tab, String(presence.lastBet.pick), t)}</b>
                  {presence.lastBet.amount ? <> · {fmtMoney(Number(presence.lastBet.amount))}</> : null}
                </span>
              ) : null}
            </section>
          ) : null}

          <div className="pp-stats-head">
            <div className="profile-period-switch is-compact" role="tablist" aria-label={t('publicProfilePeriod')}>
              <button type="button" className={period === profilePeriodFor(boardPeriod) ? 'active' : ''} onClick={() => setPeriod(profilePeriodFor(boardPeriod))}>
                {periodLabel(boardPeriod, t)}
              </button>
              <button type="button" className={period === '7d' ? 'active' : ''} onClick={() => setPeriod('7d')}>{periodLabel('7d', t)}</button>
            </div>
          </div>

          {/* The outcome is the headline; volume and rate are supporting facts. */}
          <section className="public-profile-stats pp-hero-grid">
            <ProfileStat label={t('publicProfileProfit')} value={profileQuery.isLoading ? '...' : fmtSignedBRL(stats.profit)} tone={stats.profit >= 0 ? 'green' : 'red'} />
          </section>
          <section className="public-profile-stats pp-row3">
            <ProfileStat label={t('publicProfileBets')} value={profileQuery.isLoading ? '...' : fmtInt(stats.plays)} />
            <ProfileStat label={t('publicProfileWins')} value={profileQuery.isLoading ? '...' : fmtInt(stats.wins)} tone="green" />
            <ProfileStat label={t('publicProfileWinRate')} value={profileQuery.isLoading ? '...' : `${stats.winRate}%`} />
          </section>

          {/* Raffle presence — public by design (winner/entry lists already carry
              usernames product-wide), so it is not gated by stats_visible. */}
          {profileId && !profileQuery.isLoading && !profileQuery.isError ? (
            <section className="pp-raffle">
              <span className="profile-section-label">{t('publicProfileRaffleTitle')}</span>
              {raffle.entered > 0 ? (
                <div className="public-profile-stats pp-row3">
                  <ProfileStat label={t('publicProfileRaffleEntered')} value={fmtInt(raffle.entered)} />
                  {/* Its own label: this sheet already shows a "Wins" tile for
                      bets, and two tiles reading "Wins" with different numbers
                      is a puzzle, not a stat. */}
                  <ProfileStat label={t('publicProfileRaffleWins')} value={fmtInt(raffle.wins)} tone={raffle.wins > 0 ? 'green' : undefined} />
                  <ProfileStat
                    label={t('publicProfileRaffleLast')}
                    value={raffle.lastAmount > 0 ? fmtMoney(raffle.lastAmount) : '—'}
                    tone={raffle.lastAmount > 0 ? 'gold' : undefined}
                    hint={raffle.lastAt > 0 ? profileDayLabel(raffle.lastAt) : undefined}
                  />
                </div>
              ) : (
                <ProfileInfo text={t('publicProfileRaffleNone')} />
              )}
            </section>
          ) : null}

          <section className="public-profile-recent">
            {/* The list is capped at `limit` server-side, so its own length is
                not a total — printing it bare under a "Bets 115" tile read as
                "115 bets, 5 of them", which is how the two numbers ended up
                looking like a contradiction. Say "5 of 115" when they differ. */}
            <span className="profile-section-label">
              {t('publicProfileRecentBets')}
              {bets.length > 0 ? ` · ${stats.plays > bets.length
                ? t('publicProfileRecentBetsOf').replace('%n%', fmtInt(bets.length)).replace('%total%', fmtInt(stats.plays))
                : fmtInt(bets.length)}` : ''}
            </span>
            {!profileId ? <ProfileInfo text={t('publicProfileUnavailable')} /> : null}
            {profileId && profileQuery.isLoading ? <ProfileInfo text={t('loading')} /> : null}
            {profileId && profileQuery.isError ? (
              <ProfileInfo
                text={t('publicProfileUnavailable')}
                actionLabel={t('retry')}
                onAction={() => profileQuery.refetch()}
              />
            ) : null}
            {profileId && !profileQuery.isLoading && !profileQuery.isError && !visible ? <ProfileInfo text={t('publicProfilePrivate')} /> : null}
            {profileId && !profileQuery.isLoading && !profileQuery.isError && visible && bets.length === 0 ? <ProfileInfo text={t('publicProfileNoBets')} /> : null}
            {profileId && visible ? (showAllBets ? bets : bets.slice(0, RECENT_BETS_FOLD)).map((bet, index) => <ProfileBetRow bet={bet} key={`${bet.id || index}`} />) : null}
            {profileId && visible && !showAllBets && bets.length > RECENT_BETS_FOLD ? (
              <button type="button" className="public-profile-more" onClick={() => setShowAllBets(true)}>
                {t('publicProfileSeeMore')} · {fmtInt(bets.length - RECENT_BETS_FOLD)}
              </button>
            ) : null}
          </section>
        </div>
      </section>
    </div>
  );
}

function ProfileStat({ label, value, tone, hint }: { label: string; value: string; tone?: 'green' | 'red' | 'gold'; hint?: string }) {
  return (
    <div className={tone ? `tone-${tone}` : ''}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}


function ProfileInfo({ text, actionLabel, onAction }: { text: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="public-profile-info">
      <span>{text}</span>
      {actionLabel && onAction ? (
        <button type="button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}


// Five bets say how someone plays; nine is a ledger. The rest stay one tap away.
const RECENT_BETS_FOLD = 5;

function ProfileBetRow({ bet }: { bet: PublicProfileBet }) {
  const t = useI18nStore((state) => state.t);
  const game = bet.game && GAME_LABELS[bet.game] ? t(gameNameKey(bet.game)) : String(bet.game || '-');
  const pick = bet.game && bet.tab && bet.pick ? pickLabel(bet.game, bet.tab, String(bet.pick), t) : String(bet.pick || '-');
  const currency = String(bet.currency || 'BRL').toUpperCase();
  const amount = Number(bet.amount || 0);
  const payout = Number(bet.payout || 0);
  const won = payout > 0;
  const delta = won ? payout : -amount;
  // Friendly daily round id (product-wide identity: #NNN of the local day);
  // the raw global number is only the fallback for legacy rows without a ts.
  const placedAt = Number(bet.placed_at || 0);
  // The round id meant nothing to a bystander; the day and the stake do.
  const when = placedAt ? profileDayLabel(placedAt) : '';
  return (
    <article className={`public-profile-bet-row ${won ? 'won' : 'lost'}`}>
      <div className="pb-main">
        <b className="pb-game">{game}</b>
        <em className="pb-pick">{pick}</em>
      </div>
      <div className="pb-meta">
        <span className="pb-sub">{when ? `${when} · ` : ''}{fmtProfileCurrency(amount, currency)}</span>
      </div>
      <div className="pb-result">
        <strong className={won ? 'win-text' : 'lose-text'}>{fmtSignedProfileCurrency(delta, currency)}</strong>
      </div>
    </article>
  );
}

// Live position for the profile sheet, preferring the server's fresh answer and
// falling back to the row that opened it.

function profilePresence(data: PublicProfileResponse | undefined, fallback: UiLeaderboardItem) {
  const presence = data?.presence;
  if (!presence) {
    return {
      online: fallback.online,
      roomCode: fallback.roomCode,
      lastBet: fallback.lastBet,
      copyRoomCode: fallback.copyRoomCode,
    };
  }
  return {
    online: !!presence.online,
    roomCode: String(presence.room_code ?? presence.roomCode ?? ''),
    lastBet: presence.last_bet ?? presence.lastBet ?? null,
    copyRoomCode: String(presence.copy_room_code ?? presence.copyRoomCode ?? ''),
  };
}

// A board window maps straight onto the profile; only "12h/24h" have no profile
// equivalent and fall back to the calendar day.

export function profilePeriodFor(period: LeaderboardPeriod): PublicProfilePeriod {
  if (period === '30m' || period === '1h' || period === '3h' || period === 'today') return period;
  return 'today';
}

// Lifetime raffle aggregate from the public-profile payload.

function profileRaffle(data: PublicProfileResponse | undefined) {
  const raffle = data?.raffle || {};
  return {
    entered: Math.max(0, Number(raffle.entered || 0)),
    wins: Math.max(0, Number(raffle.wins || 0)),
    lastAmount: Math.max(0, Number(raffle.last_win_amount || 0)),
    lastAt: Math.max(0, Number(raffle.last_win_at || 0)),
  };
}

// DD/MM in the viewer's local day — same convention as the archive.

function profileDayLabel(seconds: number) {
  try {
    return new Date(seconds * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}


function fmtProfileCurrency(value: number, currency: string) {
  if (currency === 'BRL') return fmtMoney(value);
  const amount = Math.abs(Number(value || 0));
  const maximumFractionDigits = amount < 1 ? 4 : 2;
  return `${amount.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits })} ${currency}`;
}


function fmtSignedProfileCurrency(value: number, currency: string) {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}${fmtProfileCurrency(Math.abs(value), currency)}`;
}

// A losing hour must read as a loss. The old board clamped profit at zero and
// still printed a green "+", so every player on it looked like a winner.

export function fmtSignedBRL(value: number) {
  const amount = Number(value || 0);
  return `${amount >= 0 ? '+' : '−'}${fmtMoney(Math.abs(amount))}`;
}


export function fmtSignedPct(value: number) {
  const pct = Number(value || 0) * 100;
  const rounded = Math.abs(pct) >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
  return `${rounded >= 0 ? '+' : '−'}${Math.abs(rounded)}%`;
}


export function profitTone(value: number) {
  if (Number(value || 0) > 0) return 'tone-green';
  if (Number(value || 0) < 0) return 'tone-red';
  return 'tone-mute';
}


export function playsLabel(plays: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  return `${fmtInt(plays)} ${plays === 1 ? t('statPlayOne') : t('statPlays')}`;
}



export function profileApiUserId(user: UiLeaderboardItem['user']) {
  const raw = String(user.id || '').trim();
  if (/^\d+$/.test(raw)) return raw;
  const prefixed = raw.match(/^u[_-](\d+)$/);
  return prefixed ? prefixed[1] : '';
}


function normalizeProfileUser(item: UiLeaderboardItem, serverUser?: LeaderboardUser) {
  const username = serverUser?.username || serverUser?.name || item.user.username || 'user';
  return {
    id: String(serverUser?.id ?? serverUser?.user_id ?? serverUser?.userId ?? serverUser?.player_id ?? serverUser?.playerId ?? item.user.id),
    username,
    initial: String(serverUser?.initial || username[0] || item.user.initial || '?').toUpperCase(),
    flag: serverUser?.flag || item.user.flag || 'BR',
    level: Number(serverUser?.vipLevel ?? serverUser?.vip_level ?? serverUser?.level ?? item.user.level ?? 1) || 1,
    avatarUrl: leaderboardAvatarUrl(serverUser) || item.user.avatarUrl || '',
    isAdmin: !!(serverUser?.isAdmin || serverUser?.is_admin || item.user.isAdmin),
  };
}


export function leaderboardAvatarUrl(user?: LeaderboardUser) {
  const direct = String(user?.avatarUrl || user?.avatar_url || '');
  if (direct) return direct;
  const defaultId = Number(user?.avatar_default_id ?? user?.avatarDefaultId ?? 0) || 0;
  return defaultId ? defaultAvatarUrl(defaultId) : '';
}


function profileStats(data?: PublicProfileResponse) {
  const stats = data?.stats || {};
  return {
    plays: Number(stats.plays || 0),
    wins: Number(stats.wins ?? stats.win_count ?? 0),
    winRate: Number(stats.win_rate ?? stats.winRate ?? 0),
    profit: Number(stats.profit_brl ?? stats.profitBrl ?? 0),
  };
}


function profileStatsVisible(data?: PublicProfileResponse) {
  return !!(data?.stats_visible ?? data?.statsVisible);
}


function profileFollowers(data?: PublicProfileResponse) {
  return Math.max(0, Number(data?.followers_count ?? data?.followersCount ?? 0) || 0);
}


function profileIsFollowing(data?: PublicProfileResponse) {
  return !!(data?.is_following ?? data?.isFollowing);
}


function profileIsSelf(data?: PublicProfileResponse) {
  return !!(data?.is_self ?? data?.isSelf);
}


function profileBets(data?: PublicProfileResponse) {
  const bets = data?.recent_bets || data?.recentBets || [];
  return Array.isArray(bets) ? bets.filter((bet) => (bet.currency || 'BRL') !== 'USDT') : [];
}


export function periodLabel(period: LeaderboardPeriod, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (period === 'today') return t('drawsToday');
  if (period === '30m') return '30 min';
  if (period === '7d') return t('bigWinsPeriod7d');
  if (period === '30d') return t('bigWinsPeriod30d');
  // "1 h", "3 h" — lower-case unit with a space, the way it is written in
  // Portuguese; "1H" read as a model number.
  return period.replace(/h$/, ' h');
}



/**
 * 把「只知道 id 和展示名」的场景拼成 sheet 需要的形状。
 *
 * <p>给 Milhar 买家名单这类地方用：它手上只有买家流里的那几个字段，没有名次、
 * 没有战绩 —— 那些本来就该由 sheet 自己去 `fetchPublicProfile` 拿。
 * **不要在调用方编造 rank / plays 之类的数字**：`rank = 0` 会让名次那一行不渲染，
 * 这正是想要的；编一个非零值会显示一个假名次。
 */
export function profileSeed(seed: {
  userId: number | string;
  username: string;
  vipLevel?: number | null;
  avatarUrl?: string;
}): UiLeaderboardItem {
  return {
    rank: 0,
    ranked: false,
    user: {
      id: String(seed.userId),
      username: seed.username,
      initial: seed.username.trim().charAt(0).toUpperCase(),
      flag: '',
      level: Number(seed.vipLevel || 0),
      avatarUrl: seed.avatarUrl,
    },
    plays: 0, wins: 0, winRate: 0, profit: 0, roi: 0, streak: 0,
    online: false, roomCode: '', lastBet: null, copyRoomCode: '', isMe: false,
    game: '', tier: null, roundNumber: null, roundLabel: '', roundStartsAt: null, eventId: null,
    isJackpot: false,
    payout: 0, stake: null, multiple: null, wonAt: 0,
  };
}
